import { Injectable, signal, computed, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SiteOption } from '../models/system-settings/access-users.model';

const SELECTED_SITE_KEY = 'selected_site_id';

@Injectable({
  providedIn: 'root',
})
export class SiteStateService {
  private platformId = inject(PLATFORM_ID);

  // ─── Signals (reactive state) ───
  /** All accessible sites for the current logged-in user */
  readonly sites = signal<SiteOption[]>([]);

  /** The currently selected site */
  readonly selectedSite = signal<SiteOption | null>(null);

  /** True when the user has more than one site available to choose from */
  readonly hasMultipleSites = computed(() => this.sites().length > 1);

  // ================= Normal Functions =================

  constructor() {
    // Restore last selected site from localStorage on app start
    this.restoreSelectedSite();
  }

  /**
   * Populates the sites list after a successful getAccessUserById call.
   * Automatically restores or selects the first site.
   * @param sites Mapped SiteOption[] from the API response
   */
  setSites(sites: SiteOption[]): void {
    this.sites.set(sites);
    this.restoreSelectedSite();
  }

  /**
   * Updates the currently selected site and persists it to localStorage.
   * @param site The site selected by the user
   */
  setSelectedSite(site: SiteOption): void {
    this.selectedSite.set(site);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(SELECTED_SITE_KEY, String(site.id));
    }
  }

  /**
   * Clears all site data — called on logout.
   */
  clear(): void {
    this.sites.set([]);
    this.selectedSite.set(null);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(SELECTED_SITE_KEY);
    }
  }

  /**
   * Restores the previously selected site from localStorage.
   * Falls back to the first site in the list if no stored selection exists.
   */
  private restoreSelectedSite(): void {
    const currentSites = this.sites();
    if (!currentSites.length) return;

    let restoredSite: SiteOption | null = null;

    if (isPlatformBrowser(this.platformId)) {
      const storedId = localStorage.getItem(SELECTED_SITE_KEY);
      if (storedId) {
        const parsedId = Number(storedId);
        restoredSite = currentSites.find((s) => s.id === parsedId) ?? null;
      }
    }

    // Default to the first site if no valid stored selection
    this.selectedSite.set(restoredSite ?? currentSites[0]);
  }

  // ================= API Functions =================
  // (None — this is a pure state service, API calls are made in the component)
}
